import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { X, Calendar, CheckCircle2, Send, FileSpreadsheet, FileText } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { formatMonto, formatFecha, formatFechaHora, hoyCaracas } from '../utils/format';
import SelectorMoneda, { useVistaMoneda } from '../components/reportes/SelectorMoneda';
import Exportar from '../components/reportes/Exportar';
import EncabezadoImpresion from '../components/reportes/EncabezadoImpresion';
import BotonAyuda from '../components/BotonAyuda';
import { saldoEnVista } from '../utils/indexacion';

function pref(moneda) {
  if (moneda === 'BS') return 'Bs ';
  if (moneda === 'BCV') return 'BCV$ ';
  return '$';
}

// Estado de una agenda: nace como borrador (se arma sola al arrastrar
// facturas), pasa a "aprobada" cuando alguien la aprueba y a "enviada" al
// mandarla por correo.
function estadoLote(l) {
  if (l?.enviado_en) return { texto: 'Enviada', clase: 'bg-green-50 text-green-700' };
  if (l?.aprobado_por_nombre) return { texto: 'Aprobada · pendiente de envío', clase: 'bg-amber-50 text-amber-700' };
  return { texto: 'Borrador', clase: 'bg-slate-100 text-slate-500' };
}

const npos = (n) => (Number(n) > 0 ? Number(n) : null);

// Mismo puente de conversión que EstadoCuenta.jsx: todo pasa por el bolívar.
// `bcv`/`mercado` son las tasas que quedaron fijas en la factura (snapshot
// del día que se cargó), igual que el resto de la facturación multimoneda.
function aVista(v, vista, moneda, bcv, mercado) {
  const x = Number(v) || 0;
  if (vista === moneda) return x;
  const b = npos(bcv);
  const m = npos(mercado);
  let bs;
  if (moneda === 'BS') bs = x;
  else if (moneda === 'USD') { if (!m) return x; bs = x * m; }
  else { if (!b) return x; bs = x * b; }
  if (vista === 'USD') return m ? bs / m : x;
  if (vista === 'BCV') return b ? bs / b : x;
  return x;
}

// Saldo de una factura ya convertido a la vista elegida (USD/BCV) — todas
// las tarjetas y subtotales usan esto, así se pueden sumar entre sí aunque
// las facturas originales estén en monedas distintas.
// Una factura indexada (#151) cuenta lo que se debe hoy.
function montoVista(f, verEn) {
  return saldoEnVista(f, verEn, aVista);
}

function sumarVista(facturas, verEn) {
  return facturas.reduce((s, f) => s + montoVista(f, verEn), 0);
}

// Antigüedad respecto a la fecha de vencimiento (fecha + días de crédito):
// el número exacto de días, no un rango. El color sí agrupa por severidad.
function aging(diasVencido) {
  if (diasVencido <= 0) return { label: diasVencido === 0 ? 'Vence hoy' : `Faltan ${-diasVencido} días`, clase: 'bg-slate-100 text-slate-500' };
  if (diasVencido <= 30) return { label: `${diasVencido} días`, clase: 'bg-slate-100 text-slate-500' };
  if (diasVencido <= 60) return { label: `${diasVencido} días`, clase: 'bg-amber-50 text-amber-700' };
  return { label: `${diasVencido} días`, clase: 'bg-red-50 text-red-700' };
}

// `onQuitar`: la tarjeta ya tiene fecha asignada — la devuelve a Pendientes.
function TarjetaFactura({ f, verEn, mostrarProveedor, arrastrando, dragHandlers, onQuitar }) {
  const ag = aging(f.dias_vencido);
  return (
    <div
      {...dragHandlers}
      className={`border border-slate-200 rounded-lg p-2.5 bg-white cursor-grab active:cursor-grabbing transition-opacity ${arrastrando ? 'opacity-40' : ''}`}
    >
      <div className="flex items-center justify-between gap-1">
        {mostrarProveedor ? (
          <p className="text-xs font-medium text-slate-600 truncate">{f.proveedor_nombre}</p>
        ) : <span />}
        {onQuitar && (
          <button onClick={onQuitar} title="Volver a Pendientes" className="text-slate-300 hover:text-red-600 shrink-0">
            <X size={13} />
          </button>
        )}
      </div>
      <p className="text-sm font-medium text-slate-800 truncate">#{f.numero}</p>
      <div className="flex items-center justify-between mt-1">
        <span className="text-sm font-semibold text-slate-800">{pref(verEn)}{formatMonto(montoVista(f, verEn))}</span>
        <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${ag.clase}`}>{ag.label}</span>
      </div>
      <p className="text-xs text-slate-400 mt-1">Vence {formatFecha(f.fecha_vencimiento)}</p>
      {f.categoria && <p className="text-xs text-slate-400 truncate mt-0.5" title={f.categoria}>{f.categoria}</p>}
      {f.notas && <p className="text-xs text-slate-400 italic truncate mt-0.5" title={f.notas}>{f.notas}</p>}
    </div>
  );
}

export default function ReporteCuentasPorPagar() {
  const { empresaActiva, empresaActivaNombre } = useAuth();
  const [facturas, setFacturas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [dragId, setDragId] = useState(null);
  const [verEn, setVerEn] = useVistaMoneda();
  // La antigüedad es un reporte aparte (Reportes → Compras); los enlaces
  // viejos con ?pestana=antiguedad se mandan allá.
  const [params] = useSearchParams();
  const aAntiguedad = params.get('pestana') === 'antiguedad';
  // Fecha que se le asigna a una factura al soltarla en la zona de pago
  // programado (no en una columna ya existente) — vive en la zona, no en
  // cada tarjeta.
  const [fechaAProgramar, setFechaAProgramar] = useState(hoyCaracas);
  // Agenda de pagos (#98): un lote por fecha con quién lo armó/aprobó/envió.
  const [lotes, setLotes] = useState([]);
  const [avisoEnvio, setAvisoEnvio] = useState(null); // resultado del último envío por correo
  const [accionLoteId, setAccionLoteId] = useState(null); // botón en curso, para deshabilitar mientras pega la API

  // `mostrarCargando=false` recarga en silencio (ej. tras soltar una
  // tarjeta): evita el flash de "Cargando..." reemplazando toda la
  // pantalla por una actualización que ya se ve reflejada al instante.
  function cargar(mostrarCargando = true) {
    if (mostrarCargando) setCargando(true);
    return api.get('/compras/cxp')
      .then(({ data }) => setFacturas(data))
      .catch((err) => setError(err.response?.data?.error || 'No se pudo cargar Cuentas por Pagar'))
      .finally(() => { if (mostrarCargando) setCargando(false); });
  }

  function cargarLotes() {
    return api.get('/agenda-pago').then(({ data }) => setLotes(data)).catch(() => {});
  }

  useEffect(() => {
    if (empresaActiva) { cargar(); cargarLotes(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaActiva]);

  const pendientes = useMemo(() => facturas.filter((f) => !f.fecha_pago_prevista), [facturas]);

  const pendientesPorProveedor = useMemo(() => {
    const grupos = new Map();
    pendientes.forEach((f) => {
      if (!grupos.has(f.proveedor_id)) grupos.set(f.proveedor_id, { nombre: f.proveedor_nombre, facturas: [] });
      grupos.get(f.proveedor_id).facturas.push(f);
    });
    return [...grupos.values()]
      .map((g) => ({ ...g, subtotal: sumarVista(g.facturas, verEn) }))
      .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
  }, [pendientes, verEn]);

  // Las columnas de pago se arman solas a partir de las fechas ya asignadas
  // — no hace falta crearlas a mano, aparecen en cuanto una factura tiene
  // esa fecha y desaparecen cuando ninguna la usa.
  const columnas = useMemo(() => {
    const fechas = new Set();
    facturas.forEach((f) => { if (f.fecha_pago_prevista) fechas.add(f.fecha_pago_prevista.slice(0, 10)); });
    return [...fechas].sort().map((fecha) => ({
      fecha,
      facturas: facturas.filter((f) => f.fecha_pago_prevista && f.fecha_pago_prevista.slice(0, 10) === fecha)
    }));
  }, [facturas]);

  async function asignar(facturaId, fechaPagoPrevista) {
    // Optimista: la tarjeta se mueve al instante; la llamada real y la
    // recarga (silenciosa) pasan de fondo. Si falla, se revierte con la
    // verdad del servidor en vez de deshacer a mano.
    setFacturas((prev) => prev.map((f) => (f.id === facturaId ? { ...f, fecha_pago_prevista: fechaPagoPrevista } : f)));
    try {
      await api.put(`/compras/${facturaId}/fecha-pago-prevista`, { fecha_pago_prevista: fechaPagoPrevista });
      // El servidor ya reconstruyó la agenda de las fechas afectadas.
      cargar(false);
      cargarLotes();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo asignar la fecha de pago');
      cargar(false);
    }
  }

  function soltarEn(fecha) {
    if (dragId == null) return;
    asignar(dragId, fecha);
    setDragId(null);
  }

  function loteDe(fecha) {
    return lotes.find((l) => String(l.fecha_pago).slice(0, 10) === fecha) || null;
  }

  async function guardarLote(fecha, facturaIds) {
    setAccionLoteId(`guardar-${fecha}`);
    try {
      const { data } = await api.post('/agenda-pago', { fecha_pago: fecha, factura_ids: facturaIds });
      await cargarLotes();
      return data.id;
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo guardar la agenda de pago');
      return null;
    } finally {
      setAccionLoteId(null);
    }
  }

  // La agenda guardada es una foto: si arrastraste facturas después de
  // guardar, el lote no las tiene. Aprobar/enviar/descargar guardan primero
  // lo que se ve en pantalla para que el archivo y el correo coincidan.
  const idsDe = (ids) => [...ids].sort((a, b) => a - b).join(',');
  const hayCambios = (lote, facturaIds) => !lote || String(lote.factura_ids || '') !== idsDe(facturaIds);
  async function loteActualizado(fecha, facturaIds) {
    const lote = loteDe(fecha);
    return hayCambios(lote, facturaIds) ? guardarLote(fecha, facturaIds) : lote.id;
  }

  async function aprobarLote(fecha, facturaIds) {
    const loteId = await loteActualizado(fecha, facturaIds);
    if (!loteId) return;
    setAccionLoteId(loteId);
    try {
      await api.post(`/agenda-pago/${loteId}/aprobar`);
      await cargarLotes();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo aprobar la agenda de pago');
    } finally {
      setAccionLoteId(null);
    }
  }

  async function enviarLote(fecha, facturaIds) {
    const loteId = await loteActualizado(fecha, facturaIds);
    if (!loteId) return;
    setAccionLoteId(loteId);
    try {
      const { data } = await api.post(`/agenda-pago/${loteId}/enviar`);
      setAvisoEnvio(data);
      await cargarLotes();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo enviar la agenda de pago por correo');
    } finally {
      setAccionLoteId(null);
    }
  }

  // Descarga un blob del backend con el nombre que manda el servidor en
  // Content-Disposition (o `nombreDefault`).
  async function descargarBlob(url, nombreDefault) {
    try {
      const resp = await api.get(url, { responseType: 'blob' });
      const cd = resp.headers?.['content-disposition'] || '';
      const m = /filename="?([^";]+)"?/i.exec(cd);
      const href = window.URL.createObjectURL(new Blob([resp.data]));
      const link = document.createElement('a');
      link.href = href;
      link.download = m ? m[1] : nombreDefault;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(href);
    } catch {
      alert('No se pudo descargar el archivo');
    }
  }

  // Descarga el Excel o PDF de una agenda guardada (mismo archivo que va
  // adjunto en el correo).
  function descargarAgenda(loteId, formato) {
    if (!loteId) return;
    return descargarBlob(`/agenda-pago/${loteId}/${formato}`, `agenda-de-pagos.${formato === 'excel' ? 'xlsx' : 'pdf'}`);
  }

  // Listado completo de "Pendientes" (sin fecha de pago asignada todavía):
  // no es un lote, se arma al vuelo en el servidor con todo lo que se ve en
  // esa columna, para no tener que arrastrar factura por factura cuando son
  // muchas (ej. Lusoplast).
  function descargarPendientes(formato) {
    return descargarBlob(`/compras/cxp/pendientes/${formato}`, `cxp-pendientes.${formato === 'excel' ? 'xlsx' : 'pdf'}`);
  }

  const totalPendientes = sumarVista(pendientes, verEn);

  // Excel de la pestaña Agenda: todas las facturas por pagar, con la fecha de
  // pago asignada (o "Pendiente") y el saldo en la moneda elegida.
  function contenidoExcelAgenda() {
    const orden = [...facturas].sort((a, b) =>
      String(a.fecha_pago_prevista || '9999').localeCompare(String(b.fecha_pago_prevista || '9999')) ||
      String(a.proveedor_nombre).localeCompare(String(b.proveedor_nombre), 'es'));
    return {
      titulo: 'Agenda de pagos',
      detalle: `Facturas por pagar · montos en ${verEn}`,
      hojas: [{
        nombre: 'Por pagar',
        columnas: [
          { titulo: 'Pago previsto', tipo: 'fecha' },
          { titulo: 'Proveedor' },
          { titulo: 'N° factura' },
          { titulo: 'Fecha', tipo: 'fecha' },
          { titulo: 'Vence', tipo: 'fecha' },
          { titulo: 'Moneda' },
          { titulo: 'Saldo original', tipo: 'monto' },
          { titulo: `Saldo ${verEn}`, tipo: 'monto' },
          { titulo: 'Categoría' },
          { titulo: 'Nota' }
        ],
        filas: orden.map((f) => [
          f.fecha_pago_prevista ? String(f.fecha_pago_prevista).slice(0, 10) : 'Pendiente',
          f.proveedor_nombre,
          f.numero,
          f.fecha ? String(f.fecha).slice(0, 10) : '',
          f.fecha_vencimiento ? String(f.fecha_vencimiento).slice(0, 10) : '',
          f.indexacion ? 'BS indexada' : f.moneda,
          f.indexacion ? f.indexacion.saldo_hoy_bs : Number(f.saldo) || 0,
          montoVista(f, verEn),
          f.categoria || '',
          f.notas || ''
        ]),
        total: ['Total', '', '', '', '', '', '', sumarVista(facturas, verEn), '', '']
      }]
    };
  }

  // Al arrastrar una tarjeta, el drag nativo del navegador bloquea la rueda
  // del mouse — sin esto no hay forma de llegar a una fecha fuera de
  // pantalla. Autoscroll manual cuando el cursor se acerca al borde.
  useEffect(() => {
    if (dragId == null) return undefined;
    function onDragOver(e) {
      const margen = 80;
      if (e.clientY < margen) window.scrollBy(0, -18);
      else if (e.clientY > window.innerHeight - margen) window.scrollBy(0, 18);
    }
    window.addEventListener('dragover', onDragOver);
    return () => window.removeEventListener('dragover', onDragOver);
  }, [dragId]);

  if (aAntiguedad) return <Navigate to="/reportes/antiguedad-por-pagar" replace />;

  return (
    <div className="space-y-6">
      <EncabezadoImpresion
        titulo="Cuentas por pagar"
        detalle={`Montos en ${verEn}`}
      />
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between print:hidden">
        <h1 className="text-2xl font-bold text-slate-800">
          Cuentas por Pagar
          {empresaActivaNombre && <span className="text-slate-400 font-normal"> · {empresaActivaNombre}</span>}
          <BotonAyuda />
        </h1>
        <div className="flex flex-wrap items-center gap-2">
          <SelectorMoneda vista={verEn} onChange={setVerEn} />
          <Exportar excel={contenidoExcelAgenda} disabled={facturas.length === 0} />
        </div>
      </div>

      {avisoEnvio && (
        <div className="flex items-start gap-2 rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-800">
          <CheckCircle2 size={18} className="mt-0.5 shrink-0" />
          <p className="flex-1">
            Agenda enviada a <strong>{avisoEnvio.correo_destino}</strong> con el Excel y el PDF adjuntos.
            {avisoEnvio.usado_respaldo && ' Esta empresa no tiene correo de tesorería configurado (Administración), por eso salió al correo de respaldo del sistema.'}
          </p>
          <button onClick={() => setAvisoEnvio(null)} title="Cerrar" className="text-green-700 hover:text-green-900"><X size={16} /></button>
        </div>
      )}

      {error && <p className="text-red-600 text-sm">{error}</p>}

      {cargando ? (
        <p className="text-slate-400 text-sm">Cargando…</p>
      ) : facturas.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-8 text-center text-slate-400">
          No hay facturas de compra pendientes de pago.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] gap-4">
          <div
            className="bg-white rounded-xl shadow-sm p-4 space-y-4"
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => soltarEn(null)}
          >
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-slate-700">Pendientes</h2>
              <div className="flex items-center gap-3">
                <span className="text-sm text-slate-500">{pref(verEn)}{formatMonto(totalPendientes)}</span>
                {pendientes.length > 0 && (
                  <>
                    <button onClick={() => descargarPendientes('excel')} title="Descargar el listado completo en Excel" className="text-slate-400 hover:text-green-600 transition-colors">
                      <FileSpreadsheet size={15} />
                    </button>
                    <button onClick={() => descargarPendientes('pdf')} title="Descargar el listado completo en PDF" className="text-slate-400 hover:text-red-600 transition-colors">
                      <FileText size={15} />
                    </button>
                  </>
                )}
              </div>
            </div>
            {pendientesPorProveedor.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-4">Todas las facturas ya tienen fecha de pago asignada.</p>
            ) : (
              pendientesPorProveedor.map((g) => (
                <div key={g.nombre}>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{g.nombre}</p>
                    <span className="text-xs text-slate-400">{pref(verEn)}{formatMonto(g.subtotal)}</span>
                  </div>
                  <div className="grid grid-cols-[repeat(auto-fill,minmax(160px,1fr))] gap-2">
                    {g.facturas.map((f) => (
                      <TarjetaFactura
                        key={f.id}
                        f={f}
                        verEn={verEn}
                        arrastrando={dragId === f.id}
                        dragHandlers={{
                          draggable: true,
                          onDragStart: () => setDragId(f.id),
                          onDragEnd: () => setDragId(null)
                        }}
                      />
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>

          <div
            className="flex flex-col gap-4 h-full"
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => soltarEn(fechaAProgramar)}
          >
            <div className="bg-white rounded-xl shadow-sm p-4 flex items-center gap-2">
              <Calendar size={15} className="text-slate-400 shrink-0" />
              <label className="text-sm text-slate-600 whitespace-nowrap">Zona de pago programado — soltar para el</label>
              <input
                type="date"
                value={fechaAProgramar}
                onChange={(e) => setFechaAProgramar(e.target.value)}
                className="border rounded-md px-2 py-1.5 text-sm"
              />
            </div>
            {columnas.map((col) => {
              const subtotal = sumarVista(col.facturas, verEn);
              const lote = loteDe(col.fecha);
              const facturaIds = col.facturas.map((f) => f.id);
              return (
                <div
                  key={col.fecha}
                  className="bg-white rounded-xl shadow-sm p-4 space-y-2"
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => { e.stopPropagation(); soltarEn(col.fecha); }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5 font-semibold text-slate-700">
                      <Calendar size={14} className="text-primary-600" />
                      Pagos para el {formatFecha(col.fecha)}
                      <span className={`ml-1 text-xs px-2 py-1 rounded-full whitespace-nowrap font-normal ${estadoLote(lote).clase}`}>{estadoLote(lote).texto}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-slate-500">{pref(verEn)}{formatMonto(subtotal)}</span>
                      <button
                        onClick={async () => descargarAgenda(await loteActualizado(col.fecha, facturaIds), 'excel')}
                        title="Descargar en Excel (guarda la lista actual primero)"
                        className="text-slate-400 hover:text-green-600 transition-colors disabled:opacity-30"
                      >
                        <FileSpreadsheet size={15} />
                      </button>
                      <button
                        onClick={async () => descargarAgenda(await loteActualizado(col.fecha, facturaIds), 'pdf')}
                        title="Descargar en PDF (guarda la lista actual primero)"
                        className="text-slate-400 hover:text-red-600 transition-colors disabled:opacity-30"
                      >
                        <FileText size={15} />
                      </button>
                      <button
                        onClick={() => aprobarLote(col.fecha, facturaIds)}
                        disabled={accionLoteId === lote?.id}
                        title={lote?.aprobado_por_nombre ? `Aprobada por ${lote.aprobado_por_nombre}` : 'Aprobar esta lista (se guarda primero)'}
                        className={`transition-colors disabled:opacity-30 ${lote?.aprobado_por_nombre ? 'text-emerald-600' : 'text-slate-400 hover:text-emerald-600'}`}
                      >
                        <CheckCircle2 size={15} />
                      </button>
                      <button
                        onClick={() => enviarLote(col.fecha, facturaIds)}
                        disabled={accionLoteId === lote?.id}
                        title={lote?.enviado_en ? `Enviada a ${lote.correo_destino} el ${formatFechaHora(lote.enviado_en)} por ${lote.enviado_por_nombre}. Clic para reenviar con Excel y PDF adjuntos` : 'Enviar por correo con Excel y PDF adjuntos (se guarda la lista primero)'}
                        className={`transition-colors disabled:opacity-30 ${lote?.enviado_en ? 'text-primary-600' : 'text-slate-400 hover:text-primary-600'}`}
                      >
                        <Send size={15} />
                      </button>
                    </div>
                  </div>
                  {lote && (
                    <p className="text-xs text-slate-400">
                      Armada por {lote.creado_por_nombre}
                      {lote.aprobado_por_nombre && ` · Aprobada por ${lote.aprobado_por_nombre}`}
                      {lote.enviado_en && ` · Enviada a ${lote.correo_destino} el ${formatFechaHora(lote.enviado_en)}`}
                    </p>
                  )}
                  <div className="grid grid-cols-[repeat(auto-fill,minmax(160px,1fr))] gap-2">
                    {col.facturas.map((f) => (
                      <TarjetaFactura
                        key={f.id}
                        f={f}
                        verEn={verEn}
                        mostrarProveedor
                        onQuitar={() => asignar(f.id, null)}
                        arrastrando={dragId === f.id}
                        dragHandlers={{
                          draggable: true,
                          onDragStart: () => setDragId(f.id),
                          onDragEnd: () => setDragId(null)
                        }}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
            {/* Zona de soltar siempre disponible: se estira para llenar
                el resto del alto de la columna, así no hace falta apuntar
                justo a una tarjeta de fecha ya existente. */}
            <div
              className="flex-1 min-h-[100px] border border-dashed border-slate-200 rounded-xl flex items-center justify-center text-sm text-slate-400 text-center px-4"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.stopPropagation(); soltarEn(fechaAProgramar); }}
            >
              Arrastrá una factura pendiente hasta acá para programarla en la fecha elegida arriba.
            </div>
          </div>
        </div>
      )}

      {lotes.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b">
            <h2 className="font-semibold text-slate-700">Historial de agendas de pago</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-left">
                <tr>
                  <th className="p-3">Fecha de pago</th>
                  <th className="p-3">Estado</th>
                  <th className="p-3">Facturas</th>
                  <th className="p-3">Armada por</th>
                  <th className="p-3">Aprobada por</th>
                  <th className="p-3">Enviada</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {lotes.map((l) => (
                  <tr key={l.id} className="border-t hover:bg-slate-50 transition-colors">
                    <td className="p-3 whitespace-nowrap font-medium">{formatFecha(l.fecha_pago)}</td>
                    <td className="p-3"><span className={`text-xs px-2 py-1 rounded-full whitespace-nowrap ${estadoLote(l).clase}`}>{estadoLote(l).texto}</span></td>
                    <td className="p-3">{l.cantidad_facturas}</td>
                    <td className="p-3 whitespace-nowrap">{l.creado_por_nombre}</td>
                    <td className="p-3 whitespace-nowrap">{l.aprobado_por_nombre || '-'}</td>
                    <td className="p-3 whitespace-nowrap">
                      {l.enviado_en ? (
                        <span className="text-primary-600">{formatFechaHora(l.enviado_en)} · {l.enviado_por_nombre} · a {l.correo_destino}</span>
                      ) : (
                        <span className="text-slate-400">Sin enviar</span>
                      )}
                    </td>
                    <td className="p-3">
                      <div className="flex items-center justify-end gap-3">
                        <button onClick={() => descargarAgenda(l.id, 'excel')} title="Descargar en Excel" className="text-slate-400 hover:text-green-600 transition-colors">
                          <FileSpreadsheet size={16} />
                        </button>
                        <button onClick={() => descargarAgenda(l.id, 'pdf')} title="Descargar en PDF" className="text-slate-400 hover:text-red-600 transition-colors">
                          <FileText size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
